import { TrendingDown, TrendingUp, type LucideIcon } from "lucide-react";
import { Card, CardContent, cn } from "../ui";

export interface DashboardCardItem {
  title: string;
  value: string;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  /** Shown next to the % when `trend` is set (e.g. "vs last week (aligned)") */
  trendCompareLabel?: string;
  iconColor?: string;
}

interface DashboardCardsProps {
  cards: DashboardCardItem[];
}

export function DashboardCards({ cards }: DashboardCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 md:gap-6">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.title}
            className="border-0 shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <h3 className="text-3xl font-semibold text-foreground mb-2">
                    {card.value}
                  </h3>
                  {card.trend && card.trendCompareLabel ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      {card.trend.isPositive ? (
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-destructive" />
                      )}
                      <span
                        className={cn(
                          "text-sm font-medium",
                          card.trend.isPositive
                            ? "text-emerald-600"
                            : "text-destructive",
                        )}
                      >
                        {card.trend.value > 0 ? "+" : ""}
                        {card.trend.value}%
                      </span>
                      <span className="text-sm text-muted-foreground">{card.trendCompareLabel}</span>
                    </div>
                  ) : null}
                </div>
                <div
                  className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    card.iconColor ?? "bg-primary",
                  )}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
